
/**
 * Public browser configuration for the FemEntity practitioner website.
 *
 * IMPORTANT:
 * Only PUBLIC Supabase values belong in this file.
 *
 * NEVER put any of the following here:
 * - SUPABASE_SERVICE_ROLE_KEY
 * - Stripe secret key (sk_...)
 * - Stripe webhook secret
 * - sb_secret_... keys
 *
 * Those belong only in Supabase Edge Function secrets.
 */

const supabaseUrl =
  'https://fstskfhauinacivdqfbd.supabase.co'

/**
 * Public Supabase publishable key used by the website.
 */
const supabaseAnonKey =
  'sb_publishable_d2zx_wCqblb0PjP4Zhte-Q_hNnSu_hB'

/**
 * Supabase Edge Functions
 */
const createAccountFunction =
  'create-practitioner-account'

const checkoutFunction =
  'create-practitioner-subscription'

const membershipStatusFunction =
  'get-practitioner-membership-status'

const connectAccountFunction =
  'create-practitioner-connect-account'

const connectStatusFunction =
  'get-practitioner-connect-status'

export const integrationConfig = {
  supabaseUrl,
  supabaseAnonKey,

  /**
   * Practitioner account creation
   */
  createAccountFunction,

  /**
   * Practitioner FemEntity membership subscription
   */
  checkoutFunction,

  /**
   * Confirms the Stripe membership after Checkout
   */
  membershipStatusFunction,

  /**
   * Creates/reuses the practitioner's Stripe Connect
   * account and starts Stripe-hosted onboarding.
   */
  connectAccountFunction,

  /**
   * Retrieves the practitioner's current Stripe Connect
   * status and synchronizes it with Supabase.
   */
  connectStatusFunction,

  /**
   * Website legal page
   */
  termsOfServiceUrl:
    '/terms-of-service',
} as const

/**
 * Minimum password length used by the practitioner
 * website signup form.
 */
export const passwordMinLength = 8

/**
 * Practitioner website routes.
 *
 * Stripe Connect onboarding returns to the existing
 * welcome page. There is no separate payments page.
 */
export const practitionerRoutes = {
  landing:
    '/practitioners',

  join:
    '/practitioners/join',

  membership:
    '/practitioners/membership',

  welcome:
    '/practitioners/welcome',
} as const

/**
 * Stripe-hosted Checkout origin.
 *
 * Used when validating/working with Stripe Checkout
 * redirects.
 */
export const stripeCheckoutOrigin =
  'https://checkout.stripe.com'

/**
 * Prevent a privileged Supabase key from accidentally
 * being placed in frontend code.
 */
function isPrivilegedKey(
  key: string,
): boolean {
  /**
   * New Supabase secret-key format.
   */
  if (
    key.startsWith('sb_secret_')
  ) {
    return true
  }

  /**
   * Legacy JWT format.
   *
   * Public anon JWTs have role "anon".
   * Service-role JWTs have role "service_role".
   */
  const parts = key.split('.')

  if (parts.length < 2) {
    return false
  }

  try {
    const payload = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const decoded = JSON.parse(
      atob(payload),
    ) as {
      role?: string
    }

    return (
      decoded.role ===
      'service_role'
    )
  } catch {
    /**
     * Publishable keys such as sb_publishable_...
     * are not JWTs, so failing to decode them is
     * expected.
     */
    return false
  }
}

/**
 * Used by practitionerApi.ts before making requests.
 *
 * The frontend is considered configured only when:
 * - Supabase URL exists
 * - public anon/publishable key exists
 * - the key is NOT privileged
 */
export function isBackendConfigured(): boolean {
  return Boolean(
    integrationConfig.supabaseUrl &&
      integrationConfig.supabaseAnonKey &&
      !isPrivilegedKey(
        integrationConfig.supabaseAnonKey,
      ),
  )
}